import React, { useState, useEffect } from "react";
import { Auth, API } from "aws-amplify";
import "../config";
import "./Users.css";

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';

import LoadingWrapper from '../components/LoadingWrapper';
import UserModal from '../components/UserModal';
import CreateUserModal from '../components/CreateUserModal';
import ModalConfirm from '../components/ModalConfirm';
import { useSnackbar } from 'notistack';
import { useContainerStyles, useCardStyles } from '../styles/commonStyles';


export default function Users(props) {
  const containerClasses = useContainerStyles();
  const cardClasses = useCardStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Snackbar
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    loadData();
  }, []);

  // endpoints

  async function getUsers() {
    let init = {
      headers: { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` }
    }
    return await API.post("API", "/Users", init);
  }

  async function getVendors() {
    let init = {
      headers: { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` }
    }
    return await API.post("API", "/Vendors", init);
  }

  async function updateUser(userId, userData) {
    const values = {
      body: {
        admin: userData.admin,
        vendor_id: userData.vendor_id,
      },
      headers: { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` }
    };
    return await API.put("API", `/User/${userId}`, values);
  }

  async function resetUserPassword(userId) {
    const values = {
      headers: { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` }
    };
    return await API.post("API", `/User/${userId}/password`, values);
  }

  async function deleteUser(userId) {
    const values = {
      headers: { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` }
    };
    return await API.del("API", `/User/${userId}`, values);
  }

  async function createUser(userData) {
    const values = {
      body: {
        email: userData.email,
        admin: parseInt(userData.admin),
        vendor_id: userData.vendor_id,
      },
      headers: { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` }
    };
    return await API.post("API", `/User`, values);
  }

  async function loadData() {
    setIsLoading(true);
    try {
      // Load vendors first to create lookup map
      const vendorsResponse = await getVendors();
      const vendorsList = vendorsResponse.data || [];
      const vendorsMap = {};
      vendorsList.forEach(vendor => {
        vendorsMap[vendor.vendor_id] = vendor.alias;
      });
      setVendors(vendorsMap);

      // Then load users
      const usersResponse = await getUsers();
      console.log("Users response:", usersResponse);
      setUsers(usersResponse.data.users || []);
    } catch (error) {
      console.error("Error loading data:", error);
      enqueueSnackbar("Kunne ikke hente data", { variant: 'error' });
      setUsers([]);
    }
    setIsLoading(false);
  }

  function renderAdminStatus(user) {
    if (user.vendor_id === "admin" && user.admin === "1") {
      return <Chip label="Admin" color="primary" size="small" />;
    }
    return <Chip label="Bruger" size="small" />;
  }

  function renderTenantStatus(vendorId) {
    if (vendorId && vendorId !== "admin") {
      const vendorAlias = vendors[vendorId];
      return <Chip label={vendorAlias || vendorId} color="secondary" size="small" />;
    }
    return <Chip label="Admin" variant="outlined" size="small" />;
  }

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  const handleSaveUser = async (updatedUser) => {
    try {
      await updateUser(updatedUser.sub, updatedUser);
      enqueueSnackbar("Bruger opdateret", { variant: 'success' });
      handleCloseModal();
      loadData(); // Reload users
    } catch (error) {
      console.error("Error updating user:", error);
      enqueueSnackbar("Kunne ikke opdatere bruger", { variant: 'error' });
    }
  };

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  const handleCreateUser = async (userData) => {
    try {
      await createUser(userData);
      enqueueSnackbar(`Bruger ${userData.email} oprettet`, { variant: 'success' });
      handleCloseCreateModal();
      loadData(); // Reload users
    } catch (error) {
      console.error("Error creating user:", error);
      enqueueSnackbar("Kunne ikke oprette bruger", { variant: 'error' });
    }
  };

  const handleOpenDeleteConfirm = (user) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = async (confirmed) => {
    if (confirmed && userToDelete) {
      try {
        await deleteUser(userToDelete.sub);
        enqueueSnackbar(`Bruger ${userToDelete.email} slettet`, { variant: 'success' });
        loadData(); // Reload users
      } catch (error) {
        console.error("Error deleting user:", error);
        enqueueSnackbar("Kunne ikke slette bruger", { variant: 'error' });
      }
    }
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
  };

  return (
    <div className={containerClasses.root}>
      <LoadingWrapper isLoading={isLoading}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className={cardClasses.root} raised>
              <CardHeader 
                title="Brugerstyring" 
                action={
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreateModal}
                  >
                    Tilføj bruger
                  </Button>
                }
              />
              <CardContent>
                {users.length === 0 ? (
                  <p>Ingen brugere fundet</p>
                ) : (
                  <Table size="small" aria-label="users-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Brugernavn</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="center">Admin Status</TableCell>
                        <TableCell align="center">Tenant</TableCell>
                        <TableCell align="center">Handlinger</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map(user => (
                        <TableRow key={user.id}>
                        <TableCell>{user.username || 'N/A'}</TableCell>
                          <TableCell>{user.email || 'N/A'}</TableCell>
                          <TableCell align="center">
                            {renderAdminStatus(user)}
                          </TableCell>
                          <TableCell align="center">
                            {renderTenantStatus(user.vendor_id)}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              onClick={() => handleEditUser(user)}
                            >
                              <EditIcon fontSize="small" />
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handleOpenDeleteConfirm(user)}
                            >
                              <DeleteIcon fontSize="small" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <UserModal
          open={modalOpen}
          user={selectedUser}
          vendors={vendors}
          onClose={handleCloseModal}
          onSave={handleSaveUser}
        />

        <CreateUserModal
          open={createModalOpen}
          vendors={vendors}
          onClose={handleCloseCreateModal}
          onCreate={handleCreateUser}
        />

        <ModalConfirm
          open={deleteConfirmOpen}
          value={false}
          question={[
            "Er du sikker på, at du vil slette denne bruger?",
            userToDelete ? `Email: ${userToDelete.email}` : ""
          ]}
          onClose={handleCloseDeleteConfirm}
        />
      </LoadingWrapper>
    </div>
  );
}
